import { Action, Dispatch } from 'redux';
import { HeaderState } from 'models/HeaderState';
import { ICart } from 'models/ICart';
import { CartItem } from 'models/CartItem';
import { rm, uid, debounceP, hasElements } from 'utils';
import { LoadingStatus } from 'models/LoadingStatus';
import {
  postAddToCart,
  postAddItems,
  deleteRemove,
  postUpdateItem,
  getIndex,
  postAddSampleToCart
} from 'services/ApiCartService';
import { deleteDiscount } from 'services/ApiCheckoutPageService';
import { basketClose, basketOpen, showProductAdded } from 'components/Basket/actions';
import { IBasicResponse } from 'models/IBasicResponse';
import { AddItemRequest } from 'models/AddItemRequest';
import { SideEffect, ThunkDispatch } from 'components/Shared/connect';
import { handleServerError } from 'components/Shared/server/request';
import {
  cartChanged,
  setCheckoutCartItemQuantity,
  singleProductSet
} from 'views/CheckoutPage/actions';
import batchAction from 'store/batching';
import { CheckoutPageModel, isCheckoutPage } from 'models/CheckoutPageModel';
import { AddItemResponse } from 'models/AddItemResponse';
import { subscribe, send } from 'store/appShell/broadcast';
import { AddSampleItemRequest } from 'models/AddSampleItemRequest';
import { isMultiQuantity } from 'components/Basket/selector';
import { CartResponse } from 'models/CartResponse';
import {
  GtmAddToCart,
  GtmClearCart,
  GtmRemoveFromCart,
  GtmVoyadoCartUpdate
} from 'store/googleTagManager/actions';
import { GtmAddToCartLocation } from 'store/googleTagManager/types';
import { RentalPeriod } from 'models/RentalPeriod';
import { useMutateCartAdd } from '@bygghemmait/queries';
import { useDispatch, useSelector } from 'react-redux';
import { getCart, getCurrentPage } from './selector';
import { getAddItemRequest } from 'views/ProductPage/actions';
import { isProductPage } from 'models/ProductPageModel';
import { AddonGroup } from 'models/AddonGroup';
import { suspendWidget } from 'views/CheckoutPage/components/deliveryWidget/util';

export const CART_LOADER = 'CART_LOADER';
export type CartStatusAction = Action & {
  status: LoadingStatus;
};
export const cartStatusLoading: CartStatusAction = {
  type: CART_LOADER,
  status: LoadingStatus.Fetching
};
export const cartStatusDone: CartStatusAction = {
  type: CART_LOADER,
  status: LoadingStatus.Completed
};

export const CART_LOADED = 'CART_LOADED';
export const CART_PRODUCT_ADD = 'CART_PRODUCT_ADD';
export const CART_PRODUCT_REMOVE = 'CART_PRODUCT_REMOVE';
export const CART_QUANTITY_UPDATE = 'CART_QUANTITY_UPDATE';
export const CART_RENTALPERIOD_UPDATE = 'CART_RENTALPERIOD_UPDATE';
export const CART_MODAL_CLOSE = 'CART_MODAL_CLOSE';
export const CART_MODAL_SHOW = 'CART_MODAL_SHOW';
export const CART_ITEM_REMOVE = 'CART_ITEM_REMOVE';
export const CART_EVENT_UPDATE = 'CART_EVENT_UPDATE';
export const CART_CHARGES = 'CART_CHARGES';
export const CART_ORIGIN_UPDATE = 'CART_ORIGIN_UPDATE';

export type CartAction = Action & CartArgs;
export type CartQuantityUpdateAction = Action & CartQuantityUpdateArgs;
export type CartRentalPeriodUpdateAction = Action & CartRentalPeriodUpdateArgs;
export type CartSummaryAction = Action & Partial<Pick<CheckoutPageModel, 'summary'>>;

interface ProductArgs {
  cartItemId: string;
}

export interface RemoveProductArgs extends ProductArgs {
  productId: string;
  gtmListName: string;
}

type CartArgs = Pick<AddItemResponse, 'cart' | 'addedProduct'>;

export const cartLoaded = ({ cart, addedProduct }: CartArgs): CartAction => ({
  type: CART_LOADED,
  cart,
  addedProduct
});

export type CartOriginUpdateAction = Pick<ICart, 'salesOriginId' | 'salesOriginClassId'> & Action;
export const cartOriginUpdate = (
  originUpdate: Pick<ICart, 'salesOriginId' | 'salesOriginClassId'>
): CartOriginUpdateAction => ({
  type: CART_ORIGIN_UPDATE,
  ...originUpdate
});

interface CartQuantityUpdateArgs {
  cartItemId: string;
  amount: number;
}

export const setItemQuantity = (args: CartQuantityUpdateArgs): CartQuantityUpdateAction => ({
  type: CART_QUANTITY_UPDATE,
  ...args
});

interface CartRentalPeriodUpdateArgs {
  cartItemId: string;
}

export const resetReturnDate = (
  args: CartRentalPeriodUpdateArgs
): CartRentalPeriodUpdateAction => ({
  type: CART_RENTALPERIOD_UPDATE,
  ...args
});

export const cartRequestWrapper = (function () {
  const requests = new Set();
  const done = (id: string, dispatch: Dispatch, emit = true) => {
    requests.delete(id);
    if (requests.size === 0) {
      emit && send(CART_LOADED);
      dispatch(cartStatusDone);
    }
  };

  return <T>(promise: Promise<T>, dispatch: Dispatch, emit?: boolean): Promise<T> => {
    const id = uid();
    if (requests.size === 0) {
      dispatch(cartStatusLoading);
    }
    requests.add(id);
    return promise
      .then(r => {
        done(id, dispatch, emit);
        return r;
      })
      .catch(error => {
        done(id, dispatch, emit);
        dispatch(cartErrorResponse(error));
        handleServerError(dispatch)(error);
        return Promise.reject(error);
      });
  };
})();

subscribe([CART_LOADED], (dispatch: ThunkDispatch) => {
  void dispatch(reloadCart());
});

export const reloadCart =
  (filterExpiredProducts = true): SideEffect =>
  (dispatch: ThunkDispatch, getState: () => HeaderState) =>
    cartRequestWrapper(getIndex(false, filterExpiredProducts), dispatch, false).then(({ cart }) => {
      dispatch(cartLoaded({ cart }));
      const state = getState();
      if (isCheckoutPage(state.currentPage)) {
        dispatch(cartChanged());

        if (state.currentPage.isSingleProduct && cart.cartItems.length > 1) {
          dispatch(singleProductSet(false));
        }
      }
    });

export const addSampleToCart =
  (input: AddSampleItemRequest): SideEffect =>
  (dispatch: ThunkDispatch, getState: () => HeaderState) =>
    cartRequestWrapper(postAddSampleToCart(input), dispatch).then(resp => {
      dispatch(batchAction(cartLoaded(resp), basketOpen(), showProductAdded()));
      const state = getState();
      if (isCheckoutPage(state.currentPage)) {
        dispatch(cartChanged());
      }
      return resp;
    });

export interface AddToCartArgs {
  input: AddItemRequest;
  gtmListName?: string;
  btnLocation?: GtmAddToCartLocation;
}
export const addToCart =
  ({ input, gtmListName, btnLocation }: AddToCartArgs): SideEffect<AddItemResponse> =>
  (dispatch: ThunkDispatch, getState: () => HeaderState) =>
    cartRequestWrapper(postAddToCart(input), dispatch).then(resp => {
      dispatch(batchAction(cartLoaded(resp), basketOpen(), showProductAdded()));
      const state = getState();
      if (isCheckoutPage(state.currentPage)) {
        dispatch(cartChanged());
      }

      if (resp.addedProduct) {
        const addedCartItem = resp.cart.cartItems.find(
          item => item.cartItemId === resp.addedProduct
        );

        if (addedCartItem) {
          dispatch(
            GtmAddToCart({
              cartItem: addedCartItem,
              itemListName: gtmListName,
              btnLocation,
              amount: input.quantity
            })
          );

          dispatch(GtmVoyadoCartUpdate(resp.cart));
        }
      }
      return resp;
    });

export const addItemsToCart =
  (input: AddItemRequest[]): SideEffect =>
  (dispatch: Dispatch) =>
    cartRequestWrapper(postAddItems(input), dispatch).then(resp => {
      dispatch(batchAction(cartLoaded(resp), basketOpen(), showProductAdded()));
      return resp;
    });

const getCartItem = (cart: ICart, cartItemId: string): Promise<CartItem> =>
  new Promise((res, rej) => {
    let item = cart.cartItems.find(item => item.cartItemId === cartItemId);
    if (item === undefined) {
      // Try to find addon
      item = cart.bobSubItemRelations
        .find(rel => rel.value.items)
        ?.value.items.find(item => item.cartItemId === cartItemId);

      if (item === undefined) {
        return rej("Item doesn't exist in cart");
      }
    }
    return res(item);
  });

const getCartItemAddons = (cart: ICart, cartItemId: string): CartItem[] => {
  const addons = cart.subItemRelations[cartItemId]?.items.map(addon => addon) || [];
  return addons;
};

function basketActionBind(dispatch: Dispatch) {
  return function updatedCart(response: CartResponse): CartResponse {
    if (!response.cart.cartItems.length) {
      dispatch(basketClose());
    }

    return response;
  };
}

export interface CartItemRemovedAction extends Action {
  cartItemId: string;
}

export const cartItemRemoved = ({ cartItemId }: ProductArgs): CartItemRemovedAction => ({
  type: CART_ITEM_REMOVE,
  cartItemId
});

export const updateCart =
  ({ cart }: CartResponse) =>
  (dispatch: ThunkDispatch) => {
    dispatch(batchAction(cartLoaded({ cart }), cartChanged(), cartStatusDone));
  };

export const cartProductRemove = ({ cartItemId, gtmListName }: RemoveProductArgs): SideEffect => {
  return (dispatch, getState) =>
    cartRequestWrapper(deleteRemove(cartItemId), dispatch)
      .then(basketActionBind(dispatch))
      .then(cart => {
        const state = getState();
        getCartItem(state.cart, cartItemId).then(item => {
          const addons = getCartItemAddons(state.cart, cartItemId);
          dispatch(
            GtmRemoveFromCart({
              cartItem: item,
              itemListName: gtmListName,
              amount: item.quantity.amount,
              addons
            })
          );
        });
        if (isCheckoutPage(state.currentPage)) {
          dispatch(cartChanged());
        }
        return cart;
      })
      .then(response => {
        dispatch(batchAction(cartLoaded(response), cartItemRemoved({ cartItemId })));
        dispatch(GtmVoyadoCartUpdate(response.cart));
      })
      .then(() => {
        const state = getState();
        if (!isCheckoutPage(state.currentPage)) {
          dispatch({ type: CART_EVENT_UPDATE, cart: getState().cart });
        }
        if (!hasElements(state.cart.cartItems)) {
          dispatch(GtmClearCart());
        }
      });
};

export const cartDiscountRemove = (code: string) => (dispatch: ThunkDispatch) => {
  void cartRequestWrapper(deleteDiscount(code), dispatch).then(
    ({ cart }) => cart && dispatch(cartLoaded({ cart }))
  );
};

interface QuantityArgs {
  cartItemId: string;
  parentId?: string | null;
  quantity: number;
  gtmListName: string;
}

export const updateQuantity =
  ({ cartItemId, parentId, quantity, gtmListName }: QuantityArgs) =>
  (dispatch: ThunkDispatch, getState: () => HeaderState) => {
    if (Number.isNaN(quantity)) {
      return Promise.resolve();
    }

    return getCartItem(getState().cart, cartItemId)
      .then(item => validateQuantity({ item, quantity }))
      .then(item =>
        postUpdateItem_(dispatch, getState, {
          id: cartItemId,
          parentId,
          quantity
        })
          .then(() => {
            const amountDelta = item.quantity.amount - quantity;
            const hasRemoved = Boolean(amountDelta > 0);
            const addons = getCartItemAddons(getState().cart, cartItemId);
            const gtmCartAction = {
              cartItem: item,
              itemListName: gtmListName,
              amount: Math.abs(amountDelta),
              addons
            };

            if (hasRemoved) {
              dispatch(GtmRemoveFromCart(gtmCartAction));
            } else {
              dispatch(GtmAddToCart(gtmCartAction));
            }
          })
          .catch(reason => {
            return Promise.reject(reason);
          })
      )
      .then(() => {
        void dispatch(setItemQuantity({ cartItemId, amount: quantity }));

        const state = getState();
        if (isCheckoutPage(state.currentPage)) {
          dispatch(setCheckoutCartItemQuantity({ cartItemId, amount: quantity }));
        }
      })
      .then(() => {
        const state = getState();
        if (!isCheckoutPage(state.currentPage)) {
          dispatch({ type: CART_EVENT_UPDATE, cart: getState().cart });
        }
      })
      .then(() => {
        dispatch(GtmVoyadoCartUpdate());
      })
      .catch((err: string) => {
        return Promise.reject(err);
      });
  };

interface RentalPeriodArgs {
  cartItemId: string;
  rentalPeriod: RentalPeriod;
}

export const updateRentalPeriod =
  ({ cartItemId, rentalPeriod }: RentalPeriodArgs) =>
  (dispatch: ThunkDispatch, getState: () => HeaderState) => {
    if (!rentalPeriod.pickupDate || !rentalPeriod.returnDate) {
      return Promise.reject(rm('checkout_invalid_rental_date'));
    }

    return getCartItem(getState().cart, cartItemId)
      .then(() => validateRentalPeriod(rentalPeriod))
      .then(() =>
        postUpdateItem_(dispatch, getState, {
          id: cartItemId,
          rentalPeriod
        })
      )
      .catch((err: string) => {
        return Promise.reject(err);
      });
  };

export interface CartModalData {
  title: string;
  messages: string[];
}

export interface CartModalAction extends Action, CartModalData {}

export const cartModalClose = (): Action => ({
  type: CART_MODAL_CLOSE
});
export const cartModalShow = (data: CartModalData): CartModalAction => ({
  type: CART_MODAL_SHOW,
  ...data
});

const cartErrorResponse = ({ errorMessage }: IBasicResponse) =>
  cartModalShow({
    title: 'Meddelande',
    messages: errorMessage ? Object.values(errorMessage) : []
  });

const POSTUPDATE_INTERVAL = 500;

interface UpdateItemRequest {
  id: string;
  parentId: string;
  quantity: number;
  rentalPeriod: RentalPeriod;
}

const postUpdateItem_ = debounceP(
  (dispatch: ThunkDispatch, getState: () => HeaderState, input: UpdateItemRequest) => {
    suspendWidget();
    return cartRequestWrapper(
      postUpdateItem(input)
        .then(({ cart }) => dispatch(cartLoaded({ cart })))
        .then(() => {
          const state = getState();
          if (isCheckoutPage(state.currentPage)) {
            dispatch(cartChanged());
          }
        }),
      dispatch
    );
  },
  POSTUPDATE_INTERVAL
);

const validateQuantity = ({
  item,
  quantity
}: {
  item: CartItem;
  quantity: number;
}): Promise<CartItem> =>
  new Promise((res, rej) => {
    const unit = isMultiQuantity(item.quantity) ? item.quantity.salesUnit : item.quantity.unit;

    if (quantity < item.quantity.lowest) {
      return rej(rm('product_validation_qtylow', item.quantity.lowest, unit));
    }
    if (quantity > item.quantity.highest) {
      return rej(rm('product_validation_qtyhigh', item.quantity.highest, unit));
    }

    res(item);
  });

const today = new Date();
const validateRentalPeriod = ({ pickupDate, returnDate }: RentalPeriod): Promise<boolean> => {
  return new Promise((res, rej) => {
    if (pickupDate < today || pickupDate >= returnDate) {
      rej(rm('checkout_invalid_rental_date'));
    }

    res(true);
  });
};

export interface AddToCartResponse {
  data: AddItemResponse;
}

type AddToCartProps = {
  productId: string;
  quantity: number;
  includeAddons: boolean;
  gtmBtnLocation?: GtmAddToCartLocation;
  gtmItemList?: string;
  inDrawer?: boolean;
  addons?: Record<string, AddonGroup>;
  configuratorVariables?: Record<string, string>;
};

type UseECPAddToCartResponse = {
  addToCart: ({
    quantity,
    gtmBtnLocation,
    gtmItemList
  }: AddToCartProps) => Promise<AddToCartResponse>;
};

/**
 * Hook that wraps the react-query hook from ECP with both GTM and error handling.
 *
 * This should be used instead of `useMutateCartAdd` to ensure consistent behavior when adding to cart
 */
export const useECPAddToCart = (): UseECPAddToCartResponse => {
  const dispatch = useDispatch();
  const { mutateAsync: addItemToCart } = useMutateCartAdd<AddToCartResponse>();
  const currentPage = useSelector(getCurrentPage);
  const cart = useSelector(getCart);

  const addToCart = ({
    productId,
    gtmBtnLocation,
    gtmItemList,
    quantity,
    includeAddons,
    addons,
    inDrawer,
    configuratorVariables
  }: AddToCartProps) => {
    let item: AddItemRequest;

    if (isProductPage(currentPage)) {
      item = includeAddons
        ? getAddItemRequest({
            currentPage,
            cartItems: cart.cartItems,
            amount: quantity,
            fullProductId: productId,
            subItemRelations: cart.subItemRelations,
            inDrawer,
            addons,
            configuratorVariables
          })
        : { id: productId, quantity };
    } else {
      item = { id: productId, quantity, getRecommendations: true };
    }

    return addItemToCart(item)
      .then(response => {
        const { data } = response;
        if (data.addedProduct) {
          const addedCartItem = data.cart.cartItems.find(
            item => item.cartItemId === data.addedProduct
          );

          const addons = getCartItemAddons(data.cart, data.addedProduct);

          if (addedCartItem) {
            dispatch(
              GtmAddToCart({
                cartItem: addedCartItem,
                itemListName: gtmItemList,
                btnLocation: gtmBtnLocation,
                amount: quantity,
                addons
              })
            );
            dispatch(GtmVoyadoCartUpdate(data.cart));
          }
        }

        return response;
      })
      .catch(e => {
        handleServerError(dispatch)(e.error);
        return Promise.reject(e.error);
      });
  };

  return { addToCart };
};
